import { useRecoilState } from "recoil"
import { QueryObserver, useQueryClient } from "react-query"
import { useEffect, useState } from "react"
import { useLocation } from "react-router-dom"
import { clickedPathsState, realTimeUpdatesState } from "../common/Refresher.state"
import { RealTimeDot } from "../App.styles"
import useCurrentRouteKey from "../hooks/useCurrentRouteKey"
import useRefreshData from "../hooks/useRefreshData"
 
export const useReactiveQueryStatus = (keyOrKeys) => {
    const queryClient = useQueryClient()

    // Normalize to an array of candidate keys
    const candidateKeys = (Array.isArray(keyOrKeys) ? keyOrKeys : [keyOrKeys]).filter(Boolean)

    // Resolve candidate keys to actual cache keys using exact or prefix match
    const resolveCacheKeys = () => {
      const all = queryClient.getQueryCache().getAll()
      const resolved = []
      for (const key of candidateKeys) {
        const segments = Array.isArray(key) ? key : [key]
        // Exact match
        const exact = queryClient.getQueryCache().find(segments)
        if (exact) {
          resolved.push(segments)
          continue
        }
        // Prefix match (handles dynamic segments like page/per_page/type)
        const match = all.find(q => Array.isArray(q.queryKey) && segments.every((seg, idx) => q.queryKey[idx] === seg))
        if (match) resolved.push(match.queryKey)
      }
      // Dedupe by JSON stringified key
      const seen = new Set()
      return resolved.filter(k => {
        const id = JSON.stringify(k)
        if (seen.has(id)) return false
        seen.add(id)
        return true
      })
    }

    const [states, setStates] = useState([])

    useEffect(() => {
      const matchedKeys = resolveCacheKeys()
      if (matchedKeys.length === 0) {
        setStates([])
        return
      }

      // Create observers for each matched key
      const observers = matchedKeys.map(k => new QueryObserver(queryClient, { queryKey: k, enabled: false }))
      const unsubscribers = observers.map(observer => observer.subscribe(result => {
        setStates(prev => {
          // Replace or append this observer's state by its key id
          const id = JSON.stringify(observer.options.queryKey)
          const byId = new Map(prev.map(s => [s.__id, s]))
          byId.set(id, { ...result, __id: id })
          return Array.from(byId.values())
        })
      }))

      // Seed with current results
      setStates(observers.map(o => ({ ...o.getCurrentResult(), __id: JSON.stringify(o.options.queryKey) })))

      return () => unsubscribers.forEach(unsub => unsub())
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryClient, JSON.stringify(candidateKeys)])

    const isFetching = states.some(s => s?.isFetching)
    const isFetched = states.length > 0 && states.every(s => s?.isFetched)
    const isSuccess = states.length > 0 && states.every(s => s?.isSuccess)

    return { isFetching, isFetched, isSuccess }
}
  
const WithRealTimeDot = Component => {
    return function Comp(props) {
        const refreshData = useRefreshData()
        const history = useLocation();
        const [, , , game_id] = history.pathname.split("/");
        const { gameId: gameIdFromProps } = props
        const gameId = gameIdFromProps || game_id
        const [clickedPaths, setClickedPaths] = useRecoilState(clickedPathsState)
        const { to: path, children, onClick, ...rest } = props
        // get the path key for the current path, e.g. /game/edit/123 -> game/edit/:id
        const pathKey = useCurrentRouteKey(path)
        
        const [realtimeUpdates] = useRecoilState(realTimeUpdatesState)

        const updatesForPath = realtimeUpdates.filter(update => update.affectedPaths.some(p => p.path === pathKey))
        const candidateKeys = updatesForPath.map(u => u?.key).filter(Boolean)

        // Get the tracking data for the query that is affected by realtime updates
        const { isFetching, isSuccess, isFetched } = useReactiveQueryStatus(candidateKeys)

        // show red-dot if an event is coming in for the current path and we're not on that page
        const shouldShowRealTimeDot = realtimeUpdates.some(
            update => update.affectedPaths.some(p => {
                // Check if current game id is in the shouldShowRealTimeDot map
                return p?.path === pathKey && p?.shouldShowRealTimeDot?.[gameId] === true
            })
        )

        const handleClick = (e) => {
            if (onClick) {
                onClick(e)
            }
            setClickedPaths(prev => [...prev, pathKey])
        }

        useEffect(() => {
            // When a path is clicked, and query has been successfully fetched,
            // remove the realtime update for that path
            // Execute in the next tick to make sure it gets removed after the screen is updated
            const requestId = window.requestAnimationFrame(() => {
                if (clickedPaths.includes(pathKey) && isFetched === true && isSuccess === true) {
                    // Clear all updates for this path (important when multiple events queued)
                    refreshData(updatesForPath, pathKey, gameId)
                    setClickedPaths(prev => prev.filter(path => path !== pathKey))
                }
            })
            return () => {
                window.cancelAnimationFrame(requestId)
            }
        }, [clickedPaths, pathKey, isFetched, isSuccess, setClickedPaths, refreshData, updatesForPath, gameId])

        return (
            <>
                <Component to={path} onClick={handleClick} {...rest}>
                    {children}
                    {shouldShowRealTimeDot ? <RealTimeDot isFetching={isFetching} /> : null}
                </Component>
            </>
        )
    }
}

export default WithRealTimeDot